import { FormInput, Popover, RadioGroup, RadioGroupDataInterface } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { createCampaignStep } from 'modules/campaigns/campaign-steps/api/campaign-step-api';
import { CampaignStepErrorResponseContentInterface } from 'modules/campaigns/campaign-steps/types/campaign-step-error-response-interface';
import { CampaignStepApiErrorsInterface, CampaignStepCreateApiInterface } from 'modules/campaigns/campaign-steps/types/campaign-step-interface';
import { getCampaignStepsEditorNextjsUrl } from 'modules/campaigns/campaign-steps/utils/get-campaign-steps-editor-nextjs-url ';
import { CampaignId } from 'modules/campaigns/types/campaign-interface';
import EmailTemplateSelector from 'modules/email/components/email-template-selector/email-template-selector';
import { useGetEmailTemplates } from 'modules/email/hooks/use-get-email-templates';
import { CreateEmailInterface } from 'modules/mailing/types/mailing-interface';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { CreateDataType } from 'shared/components/create-modal';
import { useEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-email-options';
import { CreateDataInterface } from 'shared/components/listing-layout';
import SenderEmailSelect from 'shared/components/sender-email-select';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { isValidEmail } from 'shared/utils/email-validation';
import { getEmailEditorUrl } from 'shared/utils/get-email-editor-url';
import { QuestionMarkIcon } from '@systemeio/ui/icons';
import { useRevalidateModules } from '@systemeio/util';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
const defaultValues: CreateEmailInterface = {
  subject: '',
  fromName: '',
  fromEmail: '',
  useNewEditor: true,
  template: undefined
};
const defaultErrors: CampaignStepApiErrorsInterface = {
  subject: '',
  fromName: '',
  fromEmail: ''
};
export interface UseCreateCampaignStepProps {
  campaignId: CampaignId;
  defaultSenderEmailAddress?: string;
}
export const useCreateCampaignStep = ({
  campaignId,
  defaultSenderEmailAddress
}: UseCreateCampaignStepProps) => {
  const router = useRouter();
  const [errors, setErrors] = useState<CampaignStepApiErrorsInterface>(defaultErrors);
  const [preLoadedValues, setPreLoadedValues] = useState<CreateEmailInterface>(defaultValues);
  const {
    data: emailOptions
  } = useEmailOptions();
  const {
    revalidateModules
  } = useRevalidateModules();
  const {
    templates
  } = useGetEmailTemplates();
  useEffect(() => {
    if (templates) {
      setPreLoadedValues(prev => ({
        ...prev,
        template: templates?.[0]?.id
      }));
    }
  }, [templates]);
  useEffect(() => {
    if (emailOptions) {
      setPreLoadedValues(prev_0 => ({
        ...prev_0,
        fromEmail: prev_0.fromEmail || defaultSenderEmailAddress || emailOptions.senderEmailAddress,
        fromName: prev_0.fromName || emailOptions.senderName
      }));
    }
  }, [emailOptions, defaultSenderEmailAddress]);
  const {
    t
  } = useLocoTranslation();
  const onCreate = async (data: CreateDataType<CreateEmailInterface>) => {
    try {
      if (!isValidEmail(data.fromEmail)) {
        setErrors(prev_1 => ({
          ...prev_1,
          fromEmail: t('dashboard.validation.email_not_valid')
        }));
        throw new Error();
      }
      const campaignStepData: CampaignStepCreateApiInterface = {
        mailing: {
          currentEmail: {
            subject: data.subject,
            fromEmail: data.fromEmail,
            fromName: data.fromName,
            useNewEditor: data.useNewEditor,
            template: data.template
          }
        }
      };
      const {
        data: responseData
      } = await createCampaignStep(campaignId, campaignStepData);
      if (data.useNewEditor) window.open(getEmailEditorUrl(responseData.mailing.id), '_self');else await router.push(getCampaignStepsEditorNextjsUrl({
        campaignId,
        campaignStepId: responseData.id
      }));
      await revalidateModules([RequestModuleEnum.CampaignStep], true);
    } catch (e) {
      if (e instanceof BadRequest && e.errors.fields) {
        const errors_0 = e.errors as CampaignStepErrorResponseContentInterface;
        const fields = !Array.isArray(errors_0.fields) ? errors_0.fields : undefined;
        if (fields) {
          setErrors({
            subject: fields?.mailing?.currentEmail?.subject?.join(''),
            fromName: fields?.mailing?.currentEmail?.fromName?.join(''),
            fromEmail: fields?.mailing?.currentEmail?.fromEmail?.join('')
          });
        }
      } else {
        throw e;
      }
      throw new Error();
    }
  };
  const templateTypeData: RadioGroupDataInterface<string>[] = [{
    id: 'new',
    caption: t('dashboard.email_template_selector.template_visual_editor')
  }, {
    id: 'old',
    caption: t('dashboard.email_template_selector.template_classic_editor')
  }];
  const onModalClose = () => setErrors(defaultErrors);
  const createDataConfig: CreateDataInterface<CreateEmailInterface> = {
    caption: t('dashboard.campaign_step.list.table.name'),
    requiredFields: ['subject', 'fromName', 'fromEmail'],
    onAfterLeave: onModalClose,
    onCreate: newData => onCreate({
      ...preLoadedValues,
      ...newData
    }),
    defaultValues: preLoadedValues,
    creatable: [{
      field: 'subject',
      onRender: (value, onChange) => <FormInput required key="newsletter-subject" label={<div className="flex items-center gap-2">
                {t('dashboard.campaign_step.subject_title')}{' '}
                <Popover label={<div className="[&>a]:text-brand-primary" dangerouslySetInnerHTML={{
          __html: t('dashboard.campaign_step.subject_title_tooltip')
        }} />} popoverClassName={'w-[160px] sm:w-[350px]'}>
                  {show => <QuestionMarkIcon className={`${show ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
                </Popover>
              </div>} placeholder={t('dashboard.campaign_step.subject_title')} value={value} onChange={e_0 => onChange(e_0.target.value)} maxLength={256} error={errors.subject} />
    }, {
      field: 'fromName',
      onRender: (value_0, onChange_0) => <FormInput required key="newsletter-sender-name" label={t('dashboard.campaign_step.sender_name_title')} value={value_0} onChange={e_1 => onChange_0(e_1.target.value)} maxLength={256} error={errors.fromName} />
    }, {
      field: 'fromEmail',
      onRender: (value_1, onChange_1) => <SenderEmailSelect required key="newsletter-sender-email" onChange={data_0 => data_0 && onChange_1(data_0)} value={value_1} error={errors.fromEmail} />
    }, {
      field: 'useNewEditor',
      onRender: (value_2, onChange_2, state, onChangeState) => <RadioGroup key="campaign-editor-type" options={templateTypeData} selected={value_2 ? 'new' : 'old'} onChange={value_3 => {
        if (value_3 === 'old') {
          onChangeState({
            ...state,
            useNewEditor: false,
            template: undefined
          });
        } else onChangeState({
          ...state,
          useNewEditor: true,
          template: templates?.[0]?.id
        });
      }} />
    }, {
      field: 'template',
      shouldRender: (state_0: CreateEmailInterface) => state_0.useNewEditor,
      onRender: (value_4, onChange_3) => <EmailTemplateSelector key="template" onChange={onChange_3} templateId={value_4} templates={templates} />
    }]
  };
  return createDataConfig;
};